import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Drawer, Space, Tooltip, message } from "antd";
import { LeftOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  DaySheetScope,
  DaySheetTask,
  MaterialCategorySummary,
  MaterialSaveLine,
  normaliseMaterialCategorySummary,
  normaliseMaterialSheet,
  siteDailyApis,
} from "../../../../api/SiteDaily";
import { fmtQty } from "../executionNorm";
import DaySheetTree from "./DaySheetTree";
import PendingByDateList from "./PendingByDateList";
import CivilTaskFilterBar from "./CivilTaskFilterBar";
import MaterialCategoryRollup from "./MaterialCategoryRollup";
import MaterialLinesEditor, { LineStatusFilter, LinesView, owedFor, pendingLinesOf } from "./MaterialLinesEditor";
import {
  LineDraft,
  TaskLines,
  actualAmountOf,
  draftOf,
  emptyTaskLines,
  isDirty,
  issuedTotal,
  lineKey,
} from "./materialCategories";
import {
  SheetSectionHandle,
  SheetSectionMeta,
  saveSequentially,
  sheetMeta,
  useDaySheet,
  filterCivilSheet,
} from "./daySheet";

// Material-Stores: what the stores clerk ISSUED to each task today, against the
// task's material norms. This file owns the day's tree, the per-task drafts and
// the task DRAWER: a task opens in a side drawer (not inline under its row, where
// several open tasks stacked into one long scroll), with its lines rendered by
// MaterialLinesEditor and Save & next walking the list. Drafts are per task and
// survive closing the drawer — the row says "Unsaved" until saved.

interface Props {
  projectId: number;
  date: string; // 'YYYY-MM-DD'
  scope: DaySheetScope;
  phaseId?: number;
  activityId?: number;
  reloadToken: number;
  uomOptions: Array<{ value: number; label: string }>;
  onSheetChange: (meta: SheetSectionMeta) => void;
  onDirtyChange: (count: number) => void;
}

type NavStop = { taskId: number; date: string | null };

const MaterialStoresSection = forwardRef<SheetSectionHandle, Props>(
  ({ projectId, date, scope, phaseId, activityId, reloadToken, uomOptions, onSheetChange, onDirtyChange }, ref) => {
    // Material is booked against civil-completed TASKS, not a day: the list is
    // every task with civil done, the entry is stamped with today, and the
    // server counts it against the civil work it covers.
    const { sheet, loading, error, reload } = useDaySheet(
      "materials",
      projectId,
      { date, scope, phaseId, activityId, civilDone: true },
      reloadToken
    );
    const locked = !!sheet?.dayLocked;
    // Civil-done basis: default to tasks still owed material, or every civil-done
    // task for extra / corrective entries; plus a search.
    const [pendingOnly, setPendingOnly] = useState(true);
    const [taskSearch, setTaskSearch] = useState("");
    // Owed material, or a civil date today's issue booked (listed so it can be
    // corrected) — both keep the task in "Pending material".
    const pendingOf = useCallback(
      (task: DaySheetTask) => task.pendingMaterialCount || (task.pendingMaterialDates.length > 0 ? 1 : 0),
      []
    );
    const shownSheet = useMemo(
      () => filterCivilSheet(sheet, { pendingOnly, search: taskSearch, pendingOf }),
      [sheet, pendingOnly, taskSearch, pendingOf]
    );
    const civilCounts = useMemo(() => {
      const all = (sheet?.phases ?? []).flatMap((p) => p.activities).flatMap((a) => a.tasks);
      return { all: all.length, pending: all.filter((t) => pendingOf(t) > 0).length };
    }, [sheet, pendingOf]);

    const [openTaskId, setOpenTaskId] = useState<number | null>(null);
    // The task order the drawer walks, frozen when it opens: a save can drop a
    // task from the "pending" list, and next must not jump because of it.
    // A stop is a task, or a task ON a civil date when opened from the
    // date-wise Pending list (the drawer then lands on that date).
    const [navOrder, setNavOrder] = useState<NavStop[]>([]);
    const [focusDate, setFocusDate] = useState<string | null>(null);
    const [view, setView] = useState<LinesView>("actual");
    const [statusFilter, setStatusFilter] = useState<LineStatusFilter>("all");
    const [linesByTask, setLinesByTask] = useState<Record<number, TaskLines>>({});
    // The save loop reads the LATEST drafts, not the ones its closure was born
    // with — one task's refetch must not hand the next task stale lines.
    const linesRef = useRef(linesByTask);
    linesRef.current = linesByTask;
    // A response for a day the user has since left must never land in the
    // new day's cache (a replace-set save would then copy the old day over).
    const dateRef = useRef(date);
    dateRef.current = date;

    // Lines are per task + DATE: a new day starts with nothing open.
    useEffect(() => {
      setLinesByTask({});
      setOpenTaskId(null);
    }, [projectId, date]);

    // ---- the day by category (target vs actual) ---------------------------
    // Lines load lazily per task, so the day's totals cannot be composed from
    // what the clerk happened to open — the server rolls up the sheet's own task
    // set. Re-fetched on the same filters as the tree, and after every save.
    const [summary, setSummary] = useState<MaterialCategorySummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [savedToken, setSavedToken] = useState(0);
    useEffect(() => {
      if (!projectId || !date) return;
      let cancelled = false;
      setSummaryLoading(true);
      siteDailyApis
        .materialCategorySummary(projectId, { date, scope, phaseId, activityId, civilDone: true })
        .then((res) => {
          if (cancelled) return;
          // No summary is the honest state — never leave the previous day's
          // figures fused to the new filters.
          setSummary(res?.status === true ? normaliseMaterialCategorySummary(res.data) : null);
        })
        .catch(() => !cancelled && setSummary(null))
        .finally(() => !cancelled && setSummaryLoading(false));
      return () => {
        cancelled = true;
      };
    }, [projectId, date, scope, phaseId, activityId, reloadToken, savedToken]);

    const summaryByTask = useMemo(() => {
      const map = new Map<number, MaterialCategorySummary["tasks"][number]>();
      (summary?.tasks || []).forEach((task) => map.set(task.taskId, task));
      return map;
    }, [summary]);

    useEffect(() => {
      onSheetChange(sheetMeta(sheet, loading));
    }, [sheet, loading, onSheetChange]);

    const dirtyIds = useMemo(
      () =>
        Object.entries(linesByTask)
          .filter(([, entry]) => isDirty(entry))
          .map(([taskId]) => Number(taskId)),
      [linesByTask]
    );
    useEffect(() => {
      onDirtyChange(dirtyIds.length);
    }, [dirtyIds, onDirtyChange]);

    const patchTask = useCallback((taskId: number, patch: Partial<TaskLines>) => {
      setLinesByTask((current) => ({
        ...current,
        [taskId]: { ...(current[taskId] || emptyTaskLines()), ...patch },
      }));
    }, []);

    const fetchLines = useCallback(
      async (taskId: number, keepDraft = false) => {
        const forDate = date;
        patchTask(taskId, { loading: true, error: "" });
        const res = await siteDailyApis.getMaterials(taskId, forDate);
        if (dateRef.current !== forDate) return;
        if (res?.status !== true) {
          patchTask(taskId, { loading: false, error: res?.message || "Could not load material lines" });
          return;
        }
        const next = normaliseMaterialSheet(res.data);
        if (next.issueDate && next.issueDate !== forDate) return;
        const initial: Record<string, LineDraft> = {};
        next.lines.forEach((line) => {
          initial[lineKey(line)] = draftOf(line);
        });
        setLinesByTask((current) => ({
          ...current,
          [taskId]: {
            loading: false,
            error: "",
            sheet: next,
            initial,
            draft: keepDraft && current[taskId]?.draft ? { ...initial, ...current[taskId].draft } : { ...initial },
            saving: false,
          },
        }));
      },
      [date, patchTask]
    );

    const tasksById = useMemo(() => {
      const map = new Map<number, DaySheetTask>();
      (sheet?.phases || []).forEach((p) => p.activities.forEach((a) => a.tasks.forEach((t) => map.set(t.taskId, t))));
      return map;
    }, [sheet]);
    const shownOrder = useMemo<NavStop[]>(
      () =>
        (shownSheet?.phases || []).flatMap((p) =>
          p.activities.flatMap((a) => a.tasks.map((t) => ({ taskId: t.taskId, date: null })))
        ),
      [shownSheet]
    );
    // The date-wise list's order: civil date by civil date, oldest first.
    const dateOrder = useMemo<NavStop[]>(() => {
      const stops: NavStop[] = [];
      (shownSheet?.phases || []).forEach((p) =>
        p.activities.forEach((a) =>
          a.tasks.forEach((t) => t.pendingMaterialDates.forEach((d) => stops.push({ taskId: t.taskId, date: d.workDate })))
        )
      );
      // Stable sort: within a date, the sheet's own activity → task order.
      return stops.sort((x, y) => (x.date as string).localeCompare(y.date as string));
    }, [shownSheet]);

    // Lazy: a task's lines load the first time it is opened, and stay (with
    // their draft) while it is closed and re-opened.
    const showTask = useCallback(
      (taskId: number) => {
        setOpenTaskId(taskId);
        const entry = linesRef.current[taskId];
        // Absent, or a failed earlier attempt — never cache an error.
        if (!entry || (entry.error && !entry.loading)) fetchLines(taskId);
      },
      [fetchLines]
    );
    const showStop = (stop: NavStop) => {
      setFocusDate(stop.date);
      if (stop.date) setStatusFilter("pending");
      showTask(stop.taskId);
    };
    const openFromList = (task: DaySheetTask) => {
      setNavOrder(shownOrder);
      showStop({ taskId: task.taskId, date: null });
    };
    const openFromDate = (task: DaySheetTask, workDate: string) => {
      setNavOrder(dateOrder);
      showStop({ taskId: task.taskId, date: workDate });
    };

    const setLineDraft = (taskId: number, key: string, patch: Partial<LineDraft>) => {
      setLinesByTask((current) => {
        const entry = current[taskId];
        if (!entry) return current;
        return {
          ...current,
          [taskId]: { ...entry, draft: { ...entry.draft, [key]: { ...entry.draft[key], ...patch } } },
        };
      });
    };

    const resetTask = (taskId: number) => {
      const entry = linesRef.current[taskId];
      if (!entry) return;
      patchTask(taskId, { draft: { ...entry.initial } });
    };

    const saveTask = useCallback(
      async (taskId: number, quiet = false): Promise<boolean> => {
        const entry = linesRef.current[taskId];
        if (!entry?.sheet || locked) return false;
        // Every line WITH a qty — the server drops '' / '0' and treats the
        // set as the whole day's issue for this task. Built from the sheet's
        // own line order, so the category grouping on screen cannot change
        // what is sent.
        const lines: MaterialSaveLine[] = entry.sheet.lines
          // ONE issue per material: today's qty plus what was booked against
          // earlier civil dates — the server spreads it oldest date first.
          .map((line) => ({ line, draft: entry.draft[lineKey(line)] }))
          .filter(({ draft }) => draft && issuedTotal(draft) !== "")
          .map(({ line, draft }) => ({
            materialId: line.materialId,
            ...(line.taskBoqId ? { taskBoqId: line.taskBoqId } : {}),
            qtyIssued: issuedTotal(draft),
            ...(draft.remarks.trim() ? { remarks: draft.remarks.trim() } : {}),
            ...(draft.uomId ? { uomId: draft.uomId } : {}),
            ...(draft.rate !== "" ? { rate: draft.rate, rateSource: draft.rateSource || "ENTERED" } : {}),
            // Booked on the civil date it was entered for, exactly.
            civilDates: Object.entries(draft.pendingDays)
              .filter(([, qty]) => qty !== "" && Number(qty) > 0)
              .map(([civilDate, qty]) => ({ civilDate, qty })),
          }));
        patchTask(taskId, { saving: true });
        try {
          const res = await siteDailyApis.saveMaterials({ taskId, issueDate: date, lines, byCivilDate: true });
          if (res?.status !== true) {
            message.error(res?.message || "Could not save the material issue");
            patchTask(taskId, { saving: false });
            return false;
          }
          if (!quiet) message.success(res.data?.message || "Material issue saved");
          if (res.data?.warning) message.warning(res.data.warning, 6);
          await fetchLines(taskId);
          reload();
          setSavedToken((t) => t + 1);
          return true;
        } catch (err: any) {
          message.error(err?.message || "Could not save the material issue");
          patchTask(taskId, { saving: false });
          return false;
        }
      },
      [date, locked, patchTask, fetchLines, reload]
    );

    const taskLabel = useCallback(
      (taskId: number) => {
        for (const phase of sheet?.phases || []) {
          for (const activity of phase.activities) {
            const task = activity.tasks.find((t) => t.taskId === taskId);
            if (task) return task.taskSequenceId;
          }
        }
        return `Task ${taskId}`;
      },
      [sheet]
    );

    useImperativeHandle(
      ref,
      () => ({
        discardAll: () => {
          setLinesByTask({});
          setOpenTaskId(null);
        },
        saveAll: async () => {
          const ids = dirtyIds.map((id) => ({ id, label: taskLabel(id) }));
          if (!ids.length) return;
          await saveSequentially(ids, (id) => saveTask(id, true));
        },
      }),
      [dirtyIds, saveTask, taskLabel]
    );

    // ---- the task drawer ----------------------------------------------------
    const openTask = openTaskId !== null ? tasksById.get(openTaskId) : undefined;
    const openEntry = openTaskId !== null ? linesByTask[openTaskId] : undefined;
    const navIndex =
      openTaskId !== null ? navOrder.findIndex((stop) => stop.taskId === openTaskId && stop.date === focusDate) : -1;
    const prevStop = navIndex > 0 ? navOrder[navIndex - 1] : null;
    const nextStop = navIndex >= 0 && navIndex < navOrder.length - 1 ? navOrder[navIndex + 1] : null;
    const openDirty = isDirty(openEntry);
    const openPending = pendingLinesOf(openEntry, focusDate);

    const saveAndNext = async () => {
      if (openTaskId === null) return;
      // The next task is fixed BEFORE the save — the save may reorder the list.
      const target = nextStop;
      const ok = await saveTask(openTaskId);
      if (ok && target !== null) showStop(target);
    };

    const drawerTitle = openTask ? (
      <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
        <span>
          <span className="pms-exec-task-seq">{openTask.taskSequenceId}</span>
          <span className="pms-exec-task-name">{openTask.taskName}</span>
        </span>
        <span className="pms-exec-hint" style={{ fontWeight: 400 }}>
          Material issue · {dayjs(date).format("DD-MMM-YYYY")}
          {openTask.workUomName && openTask.producedTillDate !== ""
            ? ` · civil done ${fmtQty(openTask.producedTillDate)} ${openTask.workUomName}`
            : ""}
        </span>
      </span>
    ) : (
      "Material issue"
    );

    return (
      <>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
        {locked && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Day locked — the DPR for ${dayjs(date).format("DD-MMM-YYYY")} has been submitted`}
            description="Material issues for this date are read-only."
          />
        )}
        <MaterialCategoryRollup summary={summary} loading={summaryLoading} />
        <CivilTaskFilterBar
          pendingOnly={pendingOnly}
          onPendingOnlyChange={setPendingOnly}
          search={taskSearch}
          onSearchChange={setTaskSearch}
          pendingCount={civilCounts.pending}
          allCount={civilCounts.all}
          what="material"
        />
        {pendingOnly ? (
          // Pending material, DATE-WISE: civil date → tasks still owed for it.
          <PendingByDateList
            sheet={shownSheet}
            loading={loading}
            activeTaskId={openTaskId}
            activeDate={focusDate}
            onOpen={openFromDate}
            taskState={(task) => {
              const entry = linesByTask[task.taskId];
              return entry ? { dirty: isDirty(entry), saving: entry.saving } : undefined;
            }}
            emptyDescription="Nothing owed — every civil date has its material booked"
          />
        ) : (
          <DaySheetTree
            sheet={shownSheet}
            pendingCountOf={pendingOf}
            pendingLabel="material"
            loading={loading}
            expandedTaskIds={[]}
            onExpandedTaskIdsChange={() => undefined}
            renderTaskDetail={() => null}
            onTaskOpen={openFromList}
            activeTaskId={openTaskId}
            taskState={(task) => {
              const entry = linesByTask[task.taskId];
              const rollup = summaryByTask.get(task.taskId);
              // The task's material cost for the day (Σ issued qty × actual
              // rate), for the task-level ₹ and ₹ / unit on the tree row.
              //
              // Precedence: an OPEN task's live draft wins, so the figure moves as
              // the clerk types; a closed one falls back to the day rollup, which
              // covers every task. Without the fallback a closed task could only
              // say "open me".
              const draftAmount = entry?.sheet
                ? entry.sheet.lines.reduce(
                    (sum, line) => sum + (actualAmountOf(entry.draft[lineKey(line)]) ?? 0),
                    0
                  )
                : null;
              const rolledUp =
                rollup && rollup.actualAmount !== "" ? Number(rollup.actualAmount) : null;
              const amount = entry?.sheet ? draftAmount : rolledUp;
              const targetAmount =
                rollup && rollup.targetAmount !== "" ? Number(rollup.targetAmount) : null;
              const categories = rollup?.categories.map((category) => ({
                name: category.categoryName || "Uncategorized",
                target: category.targetAmount,
                actual: category.actualAmount,
              }));
              if (!entry && !rollup) return undefined;
              return {
                dirty: isDirty(entry),
                saving: !!entry?.saving,
                amount,
                targetAmount,
                categories,
              };
            }}
            showTrackedTag={!!sheet?.monitoringConfigured && sheet.scope === "ALL"}
            emptyDescription={
              <span>
                No tasks produced on this date in Civil
                <br />
                <span style={{ color: "#8094ae", fontSize: 12 }}>
                  Enter the day's execution first — this sheet lists the tasks that produced output
                  (with material norms; and tracked, when tracking is on).
                </span>
              </span>
            }
          />
        )}
        <Drawer
          open={openTaskId !== null}
          onClose={() => setOpenTaskId(null)}
          width="min(1180px, 94vw)"
          title={drawerTitle}
          className="pms-mat-drawer"
          extra={
            navOrder.length > 1 && navIndex >= 0 ? (
              <Space size={4}>
                <Button size="small" icon={<LeftOutlined />} disabled={prevStop === null} onClick={() => prevStop && showStop(prevStop)} aria-label="Previous task" />
                <span className="pms-exec-hint exec-num">
                  {navIndex + 1} / {navOrder.length}
                </span>
                <Button size="small" icon={<RightOutlined />} disabled={nextStop === null} onClick={() => nextStop && showStop(nextStop)} aria-label="Next task" />
              </Space>
            ) : null
          }
          footer={
            openTaskId === null ? null : (
              <div className="pms-mat-drawer-foot">
                <span className="pms-exec-hint">
                  {locked
                    ? "Day locked — read-only."
                    : openDirty
                      ? "Unsaved changes stay with this task if you move on — save them here or with Save all."
                      : "Rate is what the stores clerk records; amount is qty × rate."}
                </span>
                {!locked && (
                  <Space>
                    {openPending.length > 0 && (
                      <Tooltip title="Book everything still owed to earlier civil dates, date by date (see Pending). Review, then save.">
                        <Button
                          onClick={() =>
                            openPending.forEach((line) =>
                              setLineDraft(openTaskId, lineKey(line), {
                                pendingDays: {
                                  ...(openEntry?.draft[lineKey(line)]?.pendingDays ?? {}),
                                  ...owedFor(line, focusDate),
                                },
                              })
                            )
                          }
                          disabled={openEntry?.saving}
                        >
                          Fill pending ({openPending.length})
                        </Button>
                      </Tooltip>
                    )}
                    <Button onClick={() => resetTask(openTaskId)} disabled={!openDirty || openEntry?.saving}>
                      Reset
                    </Button>
                    <Button
                      icon={<SaveOutlined />}
                      onClick={() => saveTask(openTaskId)}
                      disabled={!openDirty}
                      loading={openEntry?.saving}
                    >
                      Save
                    </Button>
                    {nextStop !== null && (
                      <Button
                        type="primary"
                        onClick={openDirty ? saveAndNext : () => showStop(nextStop)}
                        loading={openEntry?.saving}
                      >
                        {openDirty ? "Save & next" : "Next task"} <RightOutlined />
                      </Button>
                    )}
                  </Space>
                )}
              </div>
            )
          }
        >
          {openTaskId !== null && (
            <MaterialLinesEditor
              // Search and category chips reset per task; view and status persist.
              key={`${openTaskId}-${focusDate ?? ""}`}
              focusDate={focusDate}
              entry={openEntry}
              locked={locked}
              uomOptions={uomOptions}
              view={view}
              onViewChange={setView}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onChange={(key, patch) => setLineDraft(openTaskId, key, patch)}
              onRetry={() => fetchLines(openTaskId)}
            />
          )}
        </Drawer>
      </>
    );
  }
);

export default MaterialStoresSection;
